import * as React from 'react';

import classNames from 'classnames';

import type {SerializedRelationshipFilter} from 'nutshell-core/deserialize-relationship';
import type {FilterObject} from 'nutshell-core/types';
import type {User} from 'nutshell-core/users/users-types';

import {Badge} from 'shells/badge/badge';
import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {PopoverForm} from 'shells/filter-capsule/popover-form/popover-form';
// @ts-expect-error - imported from .js/.jsx file
import {CloseIcon} from 'shells/icon/svg/close-icon';
import type {Model} from 'shells/selectable-list/selectable-list-row';

import {RouteContext} from '../../route-context';

import {ButtonContent} from '.';

import styles from './add-inbox.module.css';

type Props = {
    user: User | null | undefined;
    userInboxCount?: number;
    onClick?: () => void;
    addButtonText: string;
    onSubmit: (filter?: FilterObject | null | undefined) => void;
    isActive: boolean;
    // PopoverForm/filter props
    collection: Model[];
    schemaField: any;
    filter?: SerializedRelationshipFilter | string | any;
};

export function AddButton(props: Props) {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const buttonRef = React.useRef(null);

    const handleButtonClick = () => {
        if (props.user && props.onClick) {
            props.onClick();

            return;
        }

        setIsPopoverOpen(true);
    };

    const isEmbedded = React.useContext(RouteContext).isEmbedded;

    const styleName = classNames(styles['add-button'], {
        [styles['add-button--active']]: props.isActive,
        [styles['add-button--embedded']]: isEmbedded,
    });

    return (
        <>
            <div
                className='flex flex-dir-col pos-relative pointer-cursor'
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {props.user ? (
                    <button ref={buttonRef} onClick={handleButtonClick} className={styleName}>
                        <>
                            <ButtonContent
                                name={props.user.name}
                                userInboxCount={props.userInboxCount || 0}
                                isActive={props.isActive}
                                avatarUrl={props.user.avatarUrl}
                                initials={props.user.initials}
                                isHovered={isHovered}
                                isEmbedded={isEmbedded}
                            />
                            {props.user && (props.isActive || isHovered) && (
                                <Badge
                                    contents={
                                        isHovered && (
                                            <CloseIcon
                                                size={8}
                                                fill={colors.white}
                                                wrapWithDiv={false}
                                            />
                                        )
                                    }
                                    onClick={() => props.onSubmit(null)}
                                    color='grey'
                                />
                            )}
                        </>
                    </button>
                ) : (
                    <Button
                        getButtonRef={buttonRef}
                        onClick={handleButtonClick}
                        size='normal'
                        variant='text-add'
                        className={styles['text-add-button']}
                    >
                        {props.addButtonText}
                    </Button>
                )}
            </div>
            {!props.user && isPopoverOpen && (
                <PopoverForm
                    location='bottom-right'
                    collection={props.collection}
                    field={props.schemaField}
                    filter={props.filter || null}
                    anchor={buttonRef.current}
                    onClose={() => setIsPopoverOpen(false)}
                    onSubmit={props.onSubmit}
                    shouldAllowSelectNone={false}
                    hasSearch={true}
                />
            )}
        </>
    );
}
